import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Megaphone,
    Sparkles,
    Code2,
    Smartphone,
} from "lucide-react";
import { fadeUp } from "@/lib/motion";

/**
 * Tries to load a logo via simple-icons CDN.
 * If it 404s, we render a clean text-only chip with the first letter.
 */
const LogoChip = ({ name, slug }) => {
    const [errored, setErrored] = useState(false);
    const src = `https://cdn.simpleicons.org/${slug}/ffffff`;

    return (
        <div className="group flex items-center gap-3 shrink-0 px-5 py-3 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.08] hover:border-cyan-400/30 transition">
            {errored ? (
                <span className="h-7 w-7 grid place-items-center rounded-md bg-gradient-to-br from-cyan-400/30 to-violet-500/30 border border-white/10 text-cyan-300 font-bold text-sm">
                    {name.charAt(0)}
                </span>
            ) : (
                <img
                    src={src}
                    alt={name}
                    loading="lazy"
                    onError={() => setErrored(true)}
                    className="h-7 w-7 object-contain opacity-90 group-hover:opacity-100 transition"
                />
            )}
            <span className="font-mono text-sm text-zinc-300 uppercase tracking-[0.18em] whitespace-nowrap">
                {name}
            </span>
        </div>
    );
};

const Row = ({ title, subtitle, icon: Icon, items, reverse = false, speed = 40 }) => {
    const row = [...items, ...items];
    return (
        <div className="space-y-5">
            <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-lg bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 grid place-items-center">
                    <Icon className="h-5 w-5 text-cyan-300" strokeWidth={1.75} />
                </span>
                <div>
                    <h3 className="font-display font-semibold text-white text-lg md:text-xl">
                        {title}
                    </h3>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                        {subtitle}
                    </p>
                </div>
            </div>

            <div className="relative overflow-hidden">
                <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#050505] to-transparent z-10" />
                <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#050505] to-transparent z-10" />
                <motion.div
                    className="flex gap-4"
                    style={{ width: "max-content" }}
                    animate={{ x: reverse ? ["-50%", "0%"] : ["0%", "-50%"] }}
                    transition={{
                        duration: speed,
                        repeat: Infinity,
                        ease: "linear",
                    }}
                >
                    {row.map((it, i) => (
                        <LogoChip key={`${title}-${i}`} name={it.name} slug={it.slug} />
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

const groups = [
    {
        title: "Marketing Automation",
        subtitle: "CRM · WhatsApp · Email",
        icon: Megaphone,
        speed: 42,
        reverse: false,
        items: [
            { name: "AISensy", slug: "aisensy" },
            { name: "Brevo", slug: "brevo" },
            { name: "HubSpot", slug: "hubspot" },
            { name: "Zoho", slug: "zoho" },
            { name: "Mailchimp", slug: "mailchimp" },
            { name: "ActiveCampaign", slug: "activecampaign" },
            { name: "Meta Ads", slug: "meta" },
            { name: "Google Ads", slug: "googleads" },
            { name: "n8n", slug: "n8n" },
            { name: "Zapier", slug: "zapier" },
            { name: "Twilio", slug: "twilio" },
            { name: "SendGrid", slug: "sendgrid" },
        ],
    },
    {
        title: "AI & LLM Stack",
        subtitle: "Models · Tools · Inference",
        icon: Sparkles,
        speed: 38,
        reverse: true,
        items: [
            { name: "OpenAI", slug: "openai" },
            { name: "Claude", slug: "anthropic" },
            { name: "Gemini", slug: "googlegemini" },
            { name: "Perplexity", slug: "perplexity" },
            { name: "WriteSonic", slug: "writesonic" },
            { name: "Hugging Face", slug: "huggingface" },
            { name: "LangChain", slug: "langchain" },
            { name: "Ollama", slug: "ollama" },
            { name: "Replicate", slug: "replicate" },
            { name: "MidJourney", slug: "midjourney" },
            { name: "Pinecone", slug: "pinecone" },
            { name: "Runway", slug: "runwayml" },
        ],
    },
    {
        title: "Full-Stack Development",
        subtitle: "Frontend · Backend · Database",
        icon: Code2,
        speed: 44,
        reverse: false,
        items: [
            { name: "React", slug: "react" },
            { name: "Next.js", slug: "nextdotjs" },
            { name: "TypeScript", slug: "typescript" },
            { name: "TailwindCSS", slug: "tailwindcss" },
            { name: "Node.js", slug: "nodedotjs" },
            { name: "FastAPI", slug: "fastapi" },
            { name: "Python", slug: "python" },
            { name: "MongoDB", slug: "mongodb" },
            { name: "PostgreSQL", slug: "postgresql" },
            { name: "Redis", slug: "redis" },
            { name: "Docker", slug: "docker" },
            { name: "AWS", slug: "amazonwebservices" },
            { name: "Vercel", slug: "vercel" },
            { name: "Supabase", slug: "supabase" },
        ],
    },
    {
        title: "Mobile & Android Development",
        subtitle: "Native · Cross-Platform",
        icon: Smartphone,
        speed: 40,
        reverse: true,
        items: [
            { name: "Android", slug: "android" },
            { name: "Kotlin", slug: "kotlin" },
            { name: "Jetpack Compose", slug: "jetpackcompose" },
            { name: "Flutter", slug: "flutter" },
            { name: "Dart", slug: "dart" },
            { name: "React Native", slug: "react" },
            { name: "Swift", slug: "swift" },
            { name: "iOS", slug: "ios" },
            { name: "Firebase", slug: "firebase" },
            { name: "Google Play", slug: "googleplay" },
            { name: "App Store", slug: "appstore" },
            { name: "Gradle", slug: "gradle" },
        ],
    },
];

const TechStack = () => {
    return (
        <section
            id="tech-stack"
            data-testid="tech-stack-section"
            className="section py-24 md:py-32"
        >
            <motion.div
                className="orb"
                style={{
                    width: 520,
                    height: 520,
                    top: "15%",
                    left: "-180px",
                    background:
                        "radial-gradient(circle, rgba(0,229,255,0.16), transparent 60%)",
                }}
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div {...fadeUp} className="max-w-3xl mb-14">
                    <span className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300">
                        05 / Technologies We Use
                    </span>
                    <h2
                        data-testid="tech-stack-heading"
                        className="mt-4 font-display font-semibold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                    >
                        50+ tools.
                        <br />
                        <span className="text-grad-cyan">
                            One ruthless craft standard.
                        </span>
                    </h2>
                    <p className="mt-5 text-zinc-400 text-lg">
                        We don&apos;t pick tools for hype. We pick them because they help us
                        ship faster, scale further and sleep at night.
                    </p>
                </motion.div>

                <div className="space-y-12">
                    {groups.map((g) => (
                        <Row key={g.title} {...g} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TechStack;
